/**
 * Утилиты для проверки рабочих часов кофейни.
 * Поддерживает форматы API: working_hours / workingHours — строка или объект по дням недели.
 *
 * Время считаем в TZ кофейни (по умолчанию Europe/Moscow), а не в TZ устройства —
 * иначе около полуночи статус «сегодня» и открытость расходятся с графиком.
 */

const DEFAULT_SHOP_TIMEZONE = 'Europe/Moscow';

const WEEKDAY_RU = [
  'воскресенье', 'понедельник', 'вторник', 'среда',
  'четверг', 'пятница', 'суббота',
];

/** Ключи дней в API админки / бэка (CoffeeShopResponseSchema). */
const WEEKDAY_EN = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

const WEEKDAY_SHORT_EN = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

/**
 * Часовой пояс кофейни: явное поле или Europe/Moscow.
 * @param {Object} [cafe]
 * @returns {string}
 */
export function getShopTimeZone(cafe) {
  return cafe?.timezone || cafe?.time_zone || cafe?.timeZone || DEFAULT_SHOP_TIMEZONE;
}

/**
 * День недели (0=вс) и минуты от полуночи в заданной TZ.
 * @param {Date} date
 * @param {string} timeZone
 * @returns {{ dayIndex: number, minutes: number }}
 */
export function getZonedDayAndMinutes(date, timeZone = DEFAULT_SHOP_TIMEZONE) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);

    const byType = {};
    for (const p of parts) {
      if (p.type !== 'literal') byType[p.type] = p.value;
    }

    const dayIndex = WEEKDAY_SHORT_EN[byType.weekday];
    const hours = parseInt(byType.hour, 10);
    const minutes = parseInt(byType.minute, 10);

    if (
      dayIndex == null
      || Number.isNaN(hours)
      || Number.isNaN(minutes)
    ) {
      throw new Error('invalid zoned parts');
    }

    return { dayIndex, minutes: hours * 60 + minutes };
  } catch {
    return {
      dayIndex: date.getDay(),
      minutes: date.getHours() * 60 + date.getMinutes(),
    };
  }
}

/**
 * @param {Date} [date]
 * @param {string} [timeZone]
 * @returns {string}
 */
export function getWeekdayKey(date = new Date(), timeZone = DEFAULT_SHOP_TIMEZONE) {
  const { dayIndex } = getZonedDayAndMinutes(date, timeZone);
  return WEEKDAY_RU[dayIndex];
}

/**
 * @param {Date} [date]
 * @param {string} [timeZone]
 * @returns {string}
 */
export function getWeekdayKeyEn(date = new Date(), timeZone = DEFAULT_SHOP_TIMEZONE) {
  const { dayIndex } = getZonedDayAndMinutes(date, timeZone);
  return WEEKDAY_EN[dayIndex];
}

function normalizeHoursString(timeStr) {
  return String(timeStr)
    .trim()
    .toLowerCase()
    .replace(/[\u2013\u2014\u2212]/g, '-');
}

/**
 * Парсит строку времени вида "09:00" или "09:00-18:00" в минуты от полуночи.
 * @param {string} timeStr — "09:00" или "09:00-18:00"
 * @returns {{ start: number, end: number } | null}
 */
function parseTimeRange(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const trimmed = normalizeHoursString(timeStr);
  if (['выходной', 'closed', 'закрыто', '-', ''].includes(trimmed)) return null;

  const parts = trimmed.split(/\s*-\s*/).map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const start = parseTimeToMinutes(parts[0]);
    const end = parseTimeToMinutes(parts[1]);
    if (start != null && end != null) return { start, end };
  }
  if (parts.length === 1) {
    const start = parseTimeToMinutes(parts[0]);
    if (start != null) return { start, end: 24 * 60 - 1 };
  }
  return null;
}

function parseTimeToMinutes(str) {
  const match = str.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function isMinutesWithinRange(currentMinutes, range) {
  if (!range) return false;
  // Ночной интервал: 22:00-06:00
  if (range.end < range.start) {
    return currentMinutes >= range.start || currentMinutes <= range.end;
  }
  return currentMinutes >= range.start && currentMinutes <= range.end;
}

function findKeyForDayIndex(keys, dayIndex) {
  const ru = WEEKDAY_RU[dayIndex];
  const en = WEEKDAY_EN[dayIndex];
  const lower = keys.map((k) => k.trim().toLowerCase());
  let i = lower.indexOf(ru);
  if (i >= 0) return keys[i];
  i = lower.indexOf(en);
  if (i >= 0) return keys[i];
  return null;
}

function getRangeForDayIndex(value, dayIndex) {
  const keys = Object.keys(value || {});
  const key = findKeyForDayIndex(keys, dayIndex);
  const hours = key ? value[key] : null;
  if (!hours || typeof hours !== 'string') return { key: null, range: null, hours: null };
  return { key, range: parseTimeRange(hours), hours };
}

/**
 * Проверяет, открыта ли кофейня в данный момент (по TZ кофейни).
 * @param {Object} cafe — объект кофейни с полем working_hours или workingHours
 * @param {Date} [now] — момент проверки
 * @returns {{ isOpen: boolean, todayHours: string | null, reason?: string }}
 */
export function isCafeOpenNow(cafe, now = new Date()) {
  const value = cafe?.workingHours ?? cafe?.working_hours;
  const timeZone = getShopTimeZone(cafe);

  if (value == null || value === '') {
    return { isOpen: true, todayHours: null }; // нет данных — не блокируем заказ
  }

  const { dayIndex, minutes: currentMinutes } = getZonedDayAndMinutes(now, timeZone);

  if (typeof value === 'string') {
    const range = parseTimeRange(value);
    if (!range) {
      return { isOpen: false, todayHours: value, reason: 'Сегодня кофейня не работает' };
    }
    const isOpen = isMinutesWithinRange(currentMinutes, range);
    return {
      isOpen,
      todayHours: value,
      reason: isOpen ? null : 'Сейчас кофейня закрыта. Рабочие часы: ' + value,
    };
  }

  if (typeof value === 'object') {
    const today = getRangeForDayIndex(value, dayIndex);
    const prev = getRangeForDayIndex(value, (dayIndex + 6) % 7);

    // Если вчерашний график уходит за полночь (22:00-06:00) — учитываем его ранним утром
    const openFromPrevOvernight =
      prev.range
      && prev.range.end < prev.range.start
      && currentMinutes <= prev.range.end;

    if (!today.range && !openFromPrevOvernight) {
      const firstEntry = Object.entries(value)[0];
      const todayHours = firstEntry ? `${firstEntry[0]}: ${firstEntry[1]}` : null;
      return {
        isOpen: false,
        todayHours,
        reason: 'Сегодня кофейня не работает',
      };
    }

    const isOpen = openFromPrevOvernight || isMinutesWithinRange(currentMinutes, today.range);
    const todayLabel = today.hours ? `Сегодня: ${today.hours}` : null;
    return {
      isOpen,
      todayHours: todayLabel,
      reason: isOpen ? null : `Сейчас кофейня закрыта. ${todayLabel || ''}`.trim(),
    };
  }

  return { isOpen: true, todayHours: null };
}

/**
 * Текст расписания на сегодня для отображения (как в MapView).
 * @param {Object} cafe
 * @param {Date} [now]
 * @returns {string}
 */
export function getWorkingHoursText(cafe, now = new Date()) {
  if (!cafe) return '';
  const value = cafe.workingHours ?? cafe.working_hours;
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const timeZone = getShopTimeZone(cafe);
    const { dayIndex } = getZonedDayAndMinutes(now, timeZone);
    const keys = Object.keys(value);
    const k = findKeyForDayIndex(keys, dayIndex);
    if (k && value[k]) return `Сегодня: ${value[k]}`;
    const [firstDay, hours] = Object.entries(value)[0] || [];
    return hours ? `${firstDay}: ${hours}` : '';
  }
  return String(value);
}
